import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import { Close, MyLocation } from "@mui/icons-material";
import { Button, Dialog, DialogContent, Grid, Typography } from "@mui/material";

import { ColetaDomiciliarContext } from "../../../Context/ColetaDomiciarContext";

import { ModaisColetaDomiciliarContext } from "../../../Context/ModaisColetaDomiciarContext";
import { FormularioColetaDomiciliarContext } from "../../../Context/FormularioColetaDomiciliarContext";
import { api } from "../../../../../services/Api";

export default function ModalVisualizaCliente() {
  const { coleta, setColeta } = useContext(ColetaDomiciliarContext);
  const { zeraCliente } = useContext( FormularioColetaDomiciliarContext );
  const { visualizaCliente, setVisualizaCliente } = useContext(
    ModaisColetaDomiciliarContext
  );
  const [codGuia, setCodGuia] = useState("");
  const coletaIdRef = useRef(null);
  const buscandoRef = useRef(false);
  const requestAbortControllerRef = useRef(null);

  function abreMaps(localizacaoC) {
    const local = `${localizacaoC.split(" ").join("+")}`;
    window.open(`https://www.google.com/maps/search/?api=1&query=${local}`);
  }

  function abreMapsAntigo(enderecoC, endereco_numeroC, bairroC, cidadeC, estadoC, cepC) {
    const local = `${enderecoC},+${endereco_numeroC}+-+${bairroC},+${cidadeC}+-+${estadoC},+${cepC}`;
    window.open(`https://www.google.com/maps/search/?api=1&query=${local}`);
  }

  function fechaVisualizaCliente() {
    setVisualizaCliente(false);
    zeraCliente();
    setColeta("");
  }

  function dataBrasil(data) {
    const [ano, mes, dia] = data.split("-");
    return `${dia}/${mes}/${ano}`;
  }

  // Reset refs quando o modal fecha
  useEffect(() => {
    if (!visualizaCliente) {
      // Cancela requisição pendente se houver
      if (requestAbortControllerRef.current) {
        requestAbortControllerRef.current.abort();
        requestAbortControllerRef.current = null;
      }
      coletaIdRef.current = null;
      buscandoRef.current = false;
      setCodGuia("");
    }
  }, [visualizaCliente]);

  useEffect(() => {
    // Cancela requisição anterior se houver uma nova
    if (requestAbortControllerRef.current) {
      requestAbortControllerRef.current.abort();
      requestAbortControllerRef.current = null;
    }

    async function buscaCodGuia() {
      if (!visualizaCliente || !coleta || typeof coleta !== "object") {
        return;
      }

      // Calcula o ID da coleta diretamente aqui para evitar recálculos desnecessários
      const currentColetaId = coleta.id 
        ? String(coleta.id) 
        : (coleta.codigo_pedido || coleta.codigoPedido) && (coleta.posto_pedido || coleta.postoPedido)
          ? `${coleta.codigo_pedido || coleta.codigoPedido}_${coleta.posto_pedido || coleta.postoPedido}`
          : null;

      if (!currentColetaId) {
        return;
      }
      
      // Se já processamos esta coleta, não faz nada
      if (coletaIdRef.current === currentColetaId) {
        return;
      }

      // Se já tem cod_guia no objeto coleta, usa ele
      if (coleta.cod_guia) {
        setCodGuia(coleta.cod_guia.trim());
        coletaIdRef.current = currentColetaId;
        buscandoRef.current = false;
        return;
      }

      // Se já está buscando para esta mesma coleta, não faz nova requisição
      if (buscandoRef.current && coletaIdRef.current === currentColetaId) {
        return;
      }

      // Se não tem cod_guia, tenta buscar da API usando código de pedido
      const codigoPedido = coleta.codigo_pedido || coleta.codigoPedido;
      const postoPedido = coleta.posto_pedido || coleta.postoPedido;
      
      if (codigoPedido && postoPedido) {
        // Marca que está buscando para evitar requisições duplicadas
        buscandoRef.current = true;
        coletaIdRef.current = currentColetaId;
        
        // Cria um AbortController para poder cancelar a requisição se necessário
        const abortController = new AbortController();
        requestAbortControllerRef.current = abortController;
        
        try {
          const response = await api.post("/api/softlab/pedido", {
            codigo: parseInt(codigoPedido, 10),
            posto: postoPedido,
          }, {
            signal: abortController.signal
          });
          
          // Verifica se ainda é a mesma coleta antes de atualizar o estado
          const stillCurrentColetaId = coleta && typeof coleta === "object" 
            ? (coleta.id 
              ? String(coleta.id) 
              : (coleta.codigo_pedido || coleta.codigoPedido) && (coleta.posto_pedido || coleta.postoPedido)
                ? `${coleta.codigo_pedido || coleta.codigoPedido}_${coleta.posto_pedido || coleta.postoPedido}`
                : null)
            : null;
          
          if (stillCurrentColetaId === currentColetaId && !abortController.signal.aborted) {
            if (response.data && response.data.cod_guia) {
              setCodGuia(response.data.cod_guia.trim());
            } else {
              setCodGuia("");
            }
          }
        } catch (error) {
          // Ignora erros de abort ou cancelamento
          if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
            return;
          }
          console.log("Erro ao buscar cod_guia:", error);
          const stillCurrentColetaId = coleta && typeof coleta === "object" 
            ? (coleta.id 
              ? String(coleta.id) 
              : (coleta.codigo_pedido || coleta.codigoPedido) && (coleta.posto_pedido || coleta.postoPedido)
                ? `${coleta.codigo_pedido || coleta.codigoPedido}_${coleta.posto_pedido || coleta.postoPedido}`
                : null)
            : null;
          if (stillCurrentColetaId === currentColetaId && !abortController.signal.aborted) {
            setCodGuia("");
          }
        } finally {
          if (coletaIdRef.current === currentColetaId && requestAbortControllerRef.current === abortController) {
            buscandoRef.current = false;
            requestAbortControllerRef.current = null;
          }
        }
      } else {
        setCodGuia("");
        coletaIdRef.current = currentColetaId;
        buscandoRef.current = false;
      }
    }
    
    buscaCodGuia();
    
    // Cleanup: cancela requisição se o componente desmontar ou dependências mudarem
    return () => {
      if (requestAbortControllerRef.current) {
        requestAbortControllerRef.current.abort();
        requestAbortControllerRef.current = null;
      }
    };
  }, [visualizaCliente, coleta?.id, coleta?.codigo_pedido, coleta?.codigoPedido, coleta?.posto_pedido, coleta?.postoPedido]);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={visualizaCliente}
      onClose={() => {
        fechaVisualizaCliente();
      }}
    >
      <div>
        <div id="header-information-client">
          <i id="icon-info-client" className="fa fa-user-circle" aria-hidden="true" />
          <span>Informações do cliente</span>
          <button
            type="button"
            id="btn-close-info-client"
            onClick={() => fechaVisualizaCliente()}
          >
            <Close id="icon-back-info-client" />
          </button>
        </div>
        <DialogContent>
          <Grid container spacing={1}>
            {coleta.hora ? (
              <Grid item xs={12} sm={6}>
                <Typography variant="h2" id="nomeCliente">
                  Hora
                </Typography>
                <Typography variant="h8" id="nome">
                  {coleta.hora}
                </Typography>
              </Grid>
            ) : (
              ""
            )}
            {coleta.data ? (
              <Grid item xs={12} sm={6}>
                <Typography variant="h2" id="nomeCliente">
                  Data
                </Typography>
                <Typography variant="h8" id="nome">
                  {dataBrasil(coleta.data)}
                </Typography>
              </Grid>
            ) : (
              ""
            )}

            {coleta.localizador ? (
              <Grid item xs={12} sm={6}>
                <Typography variant="h2" id="nomeCliente">
                  Localizador
                </Typography>
                <Typography variant="h8" id="nome">
                  {coleta.localizador}
                </Typography>
              </Grid>
            ) : (
              ""
            )}

            {coleta.tipo_cliente ? (
              <Grid item xs={12} sm={6}>
                <Typography variant="h2" id="nomeCliente">
                  Tipo de cliente
                </Typography>
                <Typography variant="h8" id="nome">
                  {coleta.tipo_cliente}
                </Typography>
              </Grid>
            ) : (
              ""
            )}

            {coleta.nome ? (
              <Grid item xs={12} sm={6}>
                <Typography variant="h2" id="nomeCliente">
                  Nome
                </Typography>
                <Typography variant="h8" id="nome">
                  {coleta.nome}
                </Typography>
              </Grid>
            ) : (
              ""
            )}

            {coleta && typeof coleta === "object" && (coleta.cod_guia || codGuia) ? (
              <Grid item xs={12} sm={6}>
                <Typography variant="h2" id="nomeCliente">
                  Convênio
                </Typography>
                <Typography variant="h8" id="nome">
                  {coleta.cod_guia ? coleta.cod_guia.trim() : codGuia}
                </Typography>
              </Grid>
            ) : (
              ""
            )}

            {coleta.localizacao ? (
              <Grid item lg>
                <Typography variant="h2" id="nomeCliente">
                  Endereço
                </Typography>
                <Typography variant="h8" id="nome">
                  {coleta.localizacao}
                </Typography>
              </Grid>
            ) : (
              ""
            )}

            {!coleta.localizacao ? (
              <Grid item lg>
                <Typography variant="h2" id="nomeCliente">
                  Endereço
                </Typography>
                <Typography variant="h8" id="nome">
                  {coleta.endereco}, {coleta.endereco_numero}
                </Typography>
              </Grid>
            ) : (
              ""
            )}

            {coleta.complemento ? (
              <Grid item xs={12} sm={6}>
                <Typography variant="h2" id="nomeCliente">
                  Complemento
                </Typography>
                <Typography variant="h8" id="nome">
                  {coleta.complemento}
                </Typography>
              </Grid>
            ) : (
              ""
            )}

            {!coleta.localizacao ? (
              <Grid item xs={12} sm={6}>
                <Typography variant="h2" id="nomeCliente">
                  Bairro
                </Typography>
                <Typography variant="h8" id="nome">
                  {coleta.bairro}
                </Typography>
              </Grid>
            ) : (
              ""
            )}

            {!coleta.localizacao ? (
              <Grid item xs={12} sm={6}>
                <Typography variant="h2" id="nomeCliente">
                  Cidade
                </Typography>
                <Typography variant="h8" id="nome">
                  {coleta.cidade}-{coleta.estado}
                </Typography>
              </Grid>
            ) : (
              ""
            )}

            {!coleta.localizacao ? (
              <Grid item xs={12} sm={6}>
                <Typography variant="h2" id="nomeCliente">
                  CEP
                </Typography>
                <Typography variant="h8" id="nome">
                  {coleta.cep}
                </Typography>
              </Grid>
            ) : (
              ""
            )}

            {coleta.celular ? (
              <Grid item xs={12} sm={6}>
                <Typography variant="h2" id="nomeCliente">
                  Celular
                </Typography>
                <Typography variant="h8" id="nome">
                  {coleta.celular}
                </Typography>
              </Grid>
            ) : (
              ""
            )}

            {coleta.fone ? (
              <Grid item xs={12} sm={6}>
                <Typography variant="h2" id="nomeCliente">
                  Telefone
                </Typography>
                <Typography variant="h8" id="nome">
                  {coleta.fone}
                </Typography>
              </Grid>
            ) : (
              ""
            )}

            {coleta.email ? (
              <Grid item xs={12} sm={6}>
                <Typography variant="h2" id="nomeCliente">
                  Email
                </Typography>
                <Typography variant="h8" id="nome">
                  {coleta.email}
                </Typography>
              </Grid>
            ) : (
              ""
            )}

            {coleta.observacoes ? (
              <Grid item lg>
                <Typography variant="h2" id="nomeCliente">
                  Observação
                </Typography>
                <Typography variant="h8" id="nome">
                  {coleta.observacoes}
                </Typography>
              </Grid>
            ) : (
              ""
            )}
            {coleta.ponto_referencia ? (
              <Grid item xs={12} sm={6}>
                <Typography variant="h2" id="nomeCliente">
                  Ponto de referência
                </Typography>
                <Typography variant="h8" id="nome">
                  {coleta.ponto_referencia}
                </Typography>
              </Grid>
            ) : (
              ""
            )}
            {coleta.valor_recebido ? (
              <Grid item xs={12} sm={6}>
                <Typography variant="h2" id="nomeCliente">
                  Valor Recebido
                </Typography>
                <Typography variant="h8" id="nome">
                  R$
                  {coleta.valor_recebido}
                </Typography>
              </Grid>
            ) : (
              ""
            )}

            {!coleta.localizacao ? (
              <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  id="btnMaps"
                  onClick={() => {
                    abreMapsAntigo(
                      coleta.endereco,
                      coleta.endereco_numero,
                      coleta.bairro,
                      coleta.cidade,
                      coleta.estado,
                      coleta.cep
                    );
                  }}
                  startIcon={<MyLocation />}
                >
                  Abrir google maps
                </Button>
              </Grid>
            ) : (
              ""
            )}
            {coleta.localizacao ? (
              <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  id="btnMaps"
                  onClick={() => {
                    abreMaps(coleta.localizacao);
                  }}
                  startIcon={<MyLocation />}
                >
                  Abrir google maps
                </Button>
              </Grid>
            ) : (
              ""
            )}
          </Grid>
        </DialogContent>
      </div>
    </Dialog>
  );
}
